import React from 'react';
import { useSocialMediaConfig } from '../../hooks/useSocialMediaConfig';

interface SocialMediaConfigStatusProps {
  className?: string;
  showDetails?: boolean;
}

export const SocialMediaConfigStatus: React.FC<SocialMediaConfigStatusProps> = ({
  className = '',
  showDetails = false
}) => {
  const { config, loading, error } = useSocialMediaConfig();

  if (loading) {
    return (
      <div className={`bg-white border border-gray-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-medium text-gray-900">Social Media Configuration</h3>
        </div>
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-6 h-6 bg-gray-200 rounded"></div>
                <div className="w-20 h-4 bg-gray-200 rounded"></div>
              </div>
              <div className="w-16 h-4 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white border border-red-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-medium text-gray-900">Social Media Configuration</h3>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">Failed to load configuration: {error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className={`bg-white border border-gray-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-medium text-gray-900">Social Media Configuration</h3>
        </div>
        <div className="text-center py-4">
          <p className="text-gray-500">No configuration available</p>
        </div>
      </div>
    );
  }

  const platforms = [
    {
      id: 'facebook',
      name: 'Facebook',
      icon: '📘',
      status: config.platforms.facebook
    },
    {
      id: 'twitter',
      name: 'X (Twitter)',
      icon: '🐦',
      status: config.platforms.twitter
    },
    {
      id: 'instagram',
      name: 'Instagram',
      icon: '📷',
      status: config.platforms.instagram
    }
  ];

  const configuredCount = platforms.filter(p => p.status.enabled && p.status.hasCredentials).length;

  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-4 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Social Media Configuration</h3>
        <div className="flex items-center space-x-2">
          {config.testMode && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              Test Mode
            </span>
          )}
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {config.environment}
          </span>
        </div>
      </div>

      {/* Summary */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Ready for sharing:</span>
          <span className={`font-medium ${
            configuredCount > 0 ? 'text-green-600' : 'text-red-600'
          }`}>
            {configuredCount}/{platforms.length} platforms
          </span>
        </div>
        {configuredCount === 0 && (
          <p className="text-xs text-red-600 mt-1">
            No platforms are properly configured for automatic sharing
          </p>
        )}
      </div>

      {/* Platform Status */}
      <div className="space-y-3">
        {platforms.map((platform) => (
          <div
            key={platform.id}
            className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
          >
            <div className="flex items-center space-x-3">
              <span className="text-lg">{platform.icon}</span>
              <div>
                <div className="font-medium text-gray-900">{platform.name}</div>
                {showDetails && (
                  <div className="text-xs text-gray-500">
                    {platform.status.enabled ? 'Enabled' : 'Disabled'}
                    {platform.status.enabled && (
                      <>
                        {' • '}
                        {platform.status.hasCredentials ? 'Credentials configured' : 'No credentials'}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {/* Status Indicators */}
              <div className="flex items-center space-x-1">
                {/* Enabled Status */}
                <div className={`w-2 h-2 rounded-full ${
                  platform.status.enabled ? 'bg-blue-500' : 'bg-gray-300'
                }`} title={platform.status.enabled ? 'Enabled' : 'Disabled'}></div>
                
                {/* Credentials Status */}
                <div className={`w-2 h-2 rounded-full ${
                  platform.status.hasCredentials ? 'bg-green-500' : 'bg-red-500'
                }`} title={platform.status.hasCredentials ? 'Credentials configured' : 'No credentials'}></div>
              </div>

              {/* Overall Status */}
              {platform.status.enabled && platform.status.hasCredentials ? (
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-xs font-medium text-green-600">Ready</span>
                </div>
              ) : platform.status.enabled && !platform.status.hasCredentials ? (
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span className="text-xs font-medium text-yellow-600">No Credentials</span>
                </div>
              ) : (
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                  <span className="text-xs font-medium text-gray-500">Disabled</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      {showDetails && (
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="text-xs text-gray-500 space-y-1">
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span>Enabled</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Has Credentials</span>
              </div>
            </div>
            <p>Both indicators must be active for automatic sharing to work.</p>
          </div>
        </div>
      )}
    </div>
  );
};